import { useState } from 'react';
import { line, t, type ApprovalStanding, type ApprovalView } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { useDayText } from '../../shell/viewerDates';
import styles from './DocumentPage.module.css';

/** 🔒 The short standing line (T739; decision 2482): who a Pending document waits on, when it was approved, who rejected it. */
export function standingText(standing: ApprovalStanding | null | undefined, dayText: (instant: string | null | undefined) => string): string | null {
  if (!standing) return null;
  if (standing.kind === 'Pending') return line(t('approval.standing.pending'), '·', standing.awaiting ?? t('approval.standing.held'));
  if (standing.kind === 'Rejected') return standing.byName ? line(t('approval.standing.rejected'), '·', standing.byName) : t('approval.standing.rejected');
  const approved = line(t('approval.standing.approved'), dayText(standing.at));
  return standing.byName ? line(approved, '·', standing.byName) : approved;
}

/** The standing line's parts off a page's flat facts, where the server writes them as JSON (decision 2482); anything else reads as none. */
export function standingOf(value: unknown): ApprovalStanding | null {
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value) as { kind?: unknown } | null;
    return parsed && (parsed.kind === 'Pending' || parsed.kind === 'Approved' || parsed.kind === 'Rejected') ? (parsed as ApprovalStanding) : null;
  } catch {
    // silent: a fact that is not the server's JSON draws no line rather than breaking the page, and the server wrote it, so there is nothing to report.
    return null;
  }
}

/**
 * 🔒 The approval block every approvable document page draws (T131; decision
 * 0713), placed as decision 2482 places it (T739). A Not required document
 * carries no standing line. A Pending, Approved or Rejected one carries one
 * short line, with the routing's full sentence behind its hover. When the page
 * sees a routing land while it is open, a submit or a send pressed there, the
 * sentence shows as a notice that stays until the person dismisses it. The
 * server writes the sentence and the page never works one out.
 */
export function ApprovalReason({ view }: { view: ApprovalView | null | undefined }) {
  const dayText = useDayText();
  // The routing instant the page opened on; a later one is a routing made while the page was open.
  const [openedOn, setOpenedOn] = useState<string | null | undefined>(view ? (view.routedAt ?? null) : undefined);
  const [dismissed, setDismissed] = useState<string | null>(null);
  if (view && openedOn === undefined) setOpenedOn(view.routedAt ?? null);
  if (!view) return null;
  const routedAt = view.routedAt ?? null;
  const notice = view.reason && routedAt !== null && openedOn !== undefined && routedAt !== openedOn && dismissed !== routedAt;
  const standing = standingText(view.standing, dayText);
  if (!notice && !standing) return null;
  return (
    <>
      {notice ? (
        <div data-approval-notice={routedAt}>
          <AlertBanner tone="good" message={line(t('approval.reason.label'), '·', view.reason)} action={{ label: t('modal.dismiss'), onAction: () => setDismissed(routedAt) }} />
        </div>
      ) : null}
      {standing ? (
        <p className={styles.sentence} data-approval-reason="standing" data-approval-standing={view.standing?.kind} title={view.reason ?? undefined}>
          {standing}
        </p>
      ) : null}
    </>
  );
}

/**
 * 🔒 A Draft's one primary action reads what the gate's dry run decides (T223;
 * decision 0713): *Submit for approval* where a rule would hold it, and the
 * document's own Send where nothing would.
 */
export function draftSendLabel(view: ApprovalView | null | undefined, sendLabel: string): string {
  return view?.sendAction === 'submitForApproval' ? t('approval.submitForApproval') : sendLabel;
}

/** Beside Submit for approval, who it goes to and why, in the routing's own sentence. */
export function SendHint({ view }: { view: ApprovalView | null | undefined }) {
  if (view?.sendAction !== 'submitForApproval' || !view.sendHint) return null;
  return <span className={styles.sentence} data-send-hint="true">{view.sendHint}</span>;
}
